import type { Menu } from './types';

/**
 * Secciones del sidebar (cabeceras de grupo). Son las mismas que en
 * sporttia-competitions, para que el menú se lea igual en las dos
 * aplicaciones.
 */
export type SidebarSection =
    | 'principal'
    | 'agenda'
    | 'competitions'
    | 'management';

/** Orden canónico en el que se renderizan las secciones. */
export const SIDEBAR_SECTION_ORDER: SidebarSection[] = [
    'principal',
    'agenda',
    'competitions',
    'management',
];

/** Clave i18n de la cabecera de cada sección. */
export const SIDEBAR_SECTION_LABELS: Record<SidebarSection, string> = {
    principal: 'sidebar.sections.principal',
    agenda: 'sidebar.sections.agenda',
    competitions: 'sidebar.sections.competitions',
    management: 'sidebar.sections.management',
};

const SECTION_BY_MENU_ID: Record<string, SidebarSection> = {
    home: 'principal',
    users: 'principal',
    bookings: 'agenda',
    events: 'agenda',
    activities: 'agenda',
    competitions: 'competitions',
    accountability: 'management',
    scReports: 'management',
    notifications: 'management',
    help: 'management',
};

/**
 * Sección a la que pertenece un menú. Los menús que no estén en el mapa caen
 * en "gestión": es preferible que aparezcan en un grupo genérico a que
 * desaparezcan del sidebar.
 */
export function sectionForMenuId(id: string): SidebarSection {
    return SECTION_BY_MENU_ID[id] ?? 'management';
}

export interface SidebarSectionGroup<T> {
    section: SidebarSection;
    items: T[];
}

/**
 * Agrupa los menús por sección respetando el orden canónico y descartando las
 * secciones vacías. El orden de los ítems dentro de cada sección se preserva.
 */
export function groupMenusBySection<T extends Pick<Menu, 'id'>>(
    menus: T[],
): SidebarSectionGroup<T>[] {
    return SIDEBAR_SECTION_ORDER.map((section) => ({
        section,
        items: menus.filter((menu) => sectionForMenuId(menu.id) === section),
    })).filter((group) => group.items.length > 0);
}
